import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Database } from "@/integrations/supabase/types";

type TableName = keyof Database["public"]["Tables"];

interface CRUDConfig<TFormData> {
  /** Supabase table name */
  tableName: TableName;
  /** React Query cache key */
  queryKey: string;
  /** Human-readable entity label for toast messages */
  entityLabel: string;
  /** Transform form data before insert (optional) */
  transformInsert?: (data: TFormData) => Record<string, unknown>;
  /** Transform form data before update (optional) */
  transformUpdate?: (data: TFormData) => Record<string, unknown>;
  /** Callback after successful create, receives the created record */
  onCreateSuccess?: (created?: any) => void;
  /** Callback after successful update */
  onUpdateSuccess?: () => void;
  /** Callback after successful delete */
  onDeleteSuccess?: () => void;
}

/**
 * Generic hook for CRUD operations on Supabase tables.
 * Consolidates the repeated create/update/delete mutation pattern
 * used across Customers, Locations, Tractors, and Drivers pages.
 */
export function useCRUDMutations<TFormData>(config: CRUDConfig<TFormData>) {
  const queryClient = useQueryClient();
  const {
    tableName,
    queryKey,
    entityLabel,
    transformInsert,
    transformUpdate,
    onCreateSuccess,
    onUpdateSuccess,
    onDeleteSuccess,
  } = config;

  const createMutation = useMutation({
    mutationFn: async (data: TFormData) => {
      const insertData = transformInsert ? transformInsert(data) : (data as Record<string, unknown>);
      // Use type assertion to work around strict Supabase typing
      const { data: created, error } = await (supabase.from(tableName) as any).insert(insertData).select().single();
      if (error) throw error;
      return created;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`${entityLabel} created successfully`);
      onCreateSuccess?.(created);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: TFormData }) => {
      const updateData = transformUpdate ? transformUpdate(data) : (data as Record<string, unknown>);
      // Use type assertion to work around strict Supabase typing
      const { error } = await (supabase.from(tableName) as any)
        .update(updateData)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`${entityLabel} updated successfully`);
      onUpdateSuccess?.();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Use type assertion to work around strict Supabase typing
      const { error } = await (supabase.from(tableName) as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`${entityLabel} deleted successfully`);
      onDeleteSuccess?.();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return { createMutation, updateMutation, deleteMutation };
}
